import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Logo } from './Logo'
import { ScrollProgress } from './ScrollProgress'
import { ButtonLink } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icons'
import { useFocusTrap, useScrollLock } from '@/hooks/useFocusTrap'
import { useScrollDirection } from '@/hooks/usePointerFx'
import { PARTNER_CONTACT_PATH, PRIMARY_NAV, ROUTES, TERMINAL_PATH } from '@/lib/site'
import { cn } from '@/lib/cn'

/**
 * A pill that slides between nav items, sitting behind the active link and
 * following the pointer on hover. Position is measured from the DOM so it
 * stays correct at any font size or zoom level.
 */
function useSlidingIndicator(activeKey: string, hoverKey: string | null) {
  const listRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ left: number; width: number } | null>(null)

  const measure = useCallback(() => {
    const list = listRef.current
    if (!list) return

    const key = hoverKey ?? activeKey
    const target = list.querySelector<HTMLElement>(`[data-nav="${key}"]`)

    if (!target) {
      setBox(null)
      return
    }

    setBox({ left: target.offsetLeft, width: target.offsetWidth })
  }, [activeKey, hoverKey])

  // Layout effect so the pill never paints at a stale position.
  useLayoutEffect(measure, [measure])

  useEffect(() => {
    window.addEventListener('resize', measure)
    // Re-measure once webfonts land, since they change label widths.
    document.fonts?.ready.then(measure).catch(() => {})
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  return { listRef, box }
}

export function Header() {
  const [open, setOpen] = useState(false)
  const [hoverKey, setHoverKey] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const location = useLocation()
  const { direction, atTop } = useScrollDirection()

  const close = useCallback(() => setOpen(false), [])

  useFocusTrap(panelRef, open, close)
  useScrollLock(open)

  const activeRoute = PRIMARY_NAV.find((route) => location.pathname === route.path)?.key ?? ''

  const { listRef, box } = useSlidingIndicator(activeRoute, hoverKey)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!open) return
    const mq = window.matchMedia('(min-width: 1280px)')
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setOpen(false)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [open])

  // The bar retracts on the way down and comes back the moment you scroll up.
  const hidden = direction === 'down' && !open

  return (
    <header
      className={cn(
        'sticky top-0 z-50 px-3 pt-3 transition-transform duration-500 ease-brand sm:px-5 sm:pt-5',
        hidden ? '-translate-y-[140%]' : 'translate-y-0',
      )}
    >
      <div
        className={cn(
          'relative mx-auto flex w-full max-w-[1860px] items-center gap-4 rounded-control border px-3 py-2.5 transition-all duration-500 ease-brand',
          atTop
            ? 'border-white/60 bg-white/60 shadow-card backdrop-blur-lg'
            : 'border-line bg-white/85 shadow-lift backdrop-blur-2xl backdrop-saturate-150',
        )}
      >
        <Link
          to={ROUTES.home.path}
          className="group min-w-0 shrink rounded-control px-2 py-1"
          aria-label="GodEngine Business Intelligence Simulator — home"
        >
          <Logo />
        </Link>

        {/* Desktop navigation with the sliding indicator */}
        <div
          ref={listRef}
          className="relative mx-auto hidden items-center min-[1280px]:flex"
          onPointerLeave={() => setHoverKey(null)}
        >
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-y-1 rounded-control bg-gradient-to-r from-primary/12 to-violet/12 ring-1 ring-primary/15 transition-all duration-[420ms]',
              box ? 'opacity-100' : 'opacity-0',
            )}
            style={{
              left: box?.left ?? 0,
              width: box?.width ?? 0,
              transitionTimingFunction: 'cubic-bezier(.34,1.4,.4,1)',
            }}
          />

          <nav aria-label="Primary" className="relative flex items-center">
            {PRIMARY_NAV.map((route) => (
              <NavLink
                key={route.key}
                to={route.path}
                data-nav={route.key}
                onPointerEnter={() => setHoverKey(route.key)}
                onFocus={() => setHoverKey(route.key)}
                onBlur={() => setHoverKey(null)}
                className={({ isActive }) =>
                  cn(
                    'relative whitespace-nowrap rounded-control px-4 py-2.5 text-[13px] font-semibold transition-colors duration-300',
                    isActive ? 'text-primary' : 'text-muted hover:text-ink',
                  )
                }
              >
                {route.navLabel}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="ml-auto hidden flex-none items-center gap-2 min-[1280px]:flex">
          {/* Between 1280 and 1440 the bar only has room for one call to
              action; the secondary one is still reachable from the footer.
              The wrapper carries the visibility, since ButtonLink's own
              className lands on the anchor inside its magnetic wrapper. */}
          <span className="hidden min-[1440px]:inline-flex">
            <ButtonLink to={ROUTES.contact.path} variant="secondary" className="whitespace-nowrap">
              Request access
            </ButtonLink>
          </span>
          <ButtonLink to={PARTNER_CONTACT_PATH} glow className="whitespace-nowrap">
            Become a design partner
          </ButtonLink>
          {/* The way in. `hard` because /login is the product -- a separate application on
              this same domain -- and react-router would otherwise resolve it against its own
              table and render the 404. */}
          <ButtonLink
            to={TERMINAL_PATH}
            hard
            variant="secondary"
            className="whitespace-nowrap"
            aria-label="Open the terminal"
          >
            <span className="inline-flex items-center gap-2">
              <Icon name="terminal" className="h-4 w-4" />
              Open terminal
            </span>
          </ButtonLink>
        </div>

        <button
          ref={toggleRef}
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={panelId}
          className="ml-auto inline-flex h-11 w-11 flex-none items-center justify-center rounded-control border border-line bg-white text-ink transition-colors hover:bg-surface min-[1280px]:hidden"
        >
          <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
          <span aria-hidden="true" className="relative block h-4 w-5">
            {[0, 1, 2].map((line) => (
              <span
                key={line}
                className={cn(
                  'absolute left-0 block h-[1.8px] w-full rounded-full bg-current transition-all duration-300 ease-brand',
                  line === 0 && (open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'),
                  line === 1 && (open ? 'top-1/2 opacity-0' : 'top-1/2 -translate-y-1/2'),
                  line === 2 && (open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0'),
                )}
              />
            ))}
          </span>
        </button>

        <ScrollProgress />
      </div>

      {/* Full-screen mobile overlay */}
      <div
        id={panelId}
        ref={panelRef}
        hidden={!open}
        className="fixed inset-0 z-40 min-[1280px]:hidden"
      >
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={close}
          className="absolute inset-0 h-full w-full cursor-default bg-ink/40 backdrop-blur-sm motion-safe:animate-[fade-in_300ms_ease-out]"
        />

        <div className="absolute inset-x-3 top-24 max-h-[calc(100dvh-8rem)] overflow-y-auto rounded-band border border-line bg-white p-5 shadow-lift sm:inset-x-5">
          <nav aria-label="Mobile" className="grid gap-1.5">
            {PRIMARY_NAV.map((route, index) => (
              <NavLink
                key={route.key}
                to={route.path}
                style={{ transitionDelay: `${index * 45}ms` }}
                className={({ isActive }) =>
                  cn(
                    'group flex min-h-[52px] items-center justify-between rounded-card px-4 text-[16px] font-bold transition-all duration-300',
                    open ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0',
                    isActive
                      ? 'bg-gradient-to-r from-primary/12 to-violet/10 text-primary'
                      : 'text-ink hover:bg-surface',
                  )
                }
              >
                {route.navLabel}
                <span
                  aria-hidden="true"
                  className="text-muted-soft transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-5 grid gap-2 border-t border-line pt-5">
            <ButtonLink to={ROUTES.contact.path} variant="secondary" size="lg">
              Request access
            </ButtonLink>
            <ButtonLink to={PARTNER_CONTACT_PATH} size="lg" glow>
              Become a design partner
            </ButtonLink>
            <ButtonLink to={TERMINAL_PATH} hard variant="secondary" size="lg">
              <span className="inline-flex items-center gap-2">
                <Icon name="terminal" className="h-4 w-4" />
                Open terminal
              </span>
            </ButtonLink>
          </div>
        </div>
      </div>
    </header>
  )
}
